<template>
  <div id="app" :class="[darkMode ? 'darkMode' : 'lightMode']">
    <!-- Reading progress bar -->
    <div class="progress-bar" v-if="bodyText" :style="{width: scrollProgress + '%'}"></div>

    <!-- Share notification toast -->
    <transition name="toast">
      <div v-if="showShareNotification" class="share-toast">
        <i class="fas fa-check-circle" aria-hidden="true"></i>
        {{ $t('share_copied') }}
      </div>
    </transition>

    <!-- Header -->
    <header class="app-header">
      <!-- 96px WebP (5.5 KB) instead of the 512px PNG (78.7 KB) that was being
           scaled down to a 48px slot. width/height give the browser the aspect
           ratio up front, which removes the layout shift. -->
      <img alt="Legi" src="./assets/logo-96.webp" class="header-logo" width="96" height="96">

      <div class="header-selects">
        <CustomSelect v-model="readingTime" :options="readingTimeOptions" :darkMode="darkMode" />
        <CustomSelect v-model="langValue" :options="languageOptions" :darkMode="darkMode" />
        <CustomSelect v-model="selectedGenre" :options="genreOptions" :darkMode="darkMode" />
      </div>

      <div class="header-actions">
        <button @click="getStory"
                :title="$t('get_story')"
                :aria-label="$t('get_story')"
                class="btn-get-story">
          <i class="fas fa-book-open" aria-hidden="true"></i>
          <span>{{ $t('get_story') }}</span>
        </button>

        <button v-if="audio && bodyText && !speaking"
                @click="playAudio"
                :title="$t('listen_story')"
                :aria-label="$t('listen_story')"
                class="btn-secondary">
          <i class="fas fa-headphones" aria-hidden="true"></i>
          <span class="btn-label-text">{{ $t('listen') }}</span>
        </button>

        <button v-if="speaking"
                @click="stopAudio"
                :title="$t('stop_audio')"
                :aria-label="$t('stop_audio')"
                class="btn-secondary btn-stop">
          <i class="fas fa-stop" aria-hidden="true"></i>
          <span class="btn-label-text">{{ $t('stop_audio') }}</span>
        </button>

        <button v-if="bodyText"
                @click="shareStory"
                :title="$t('share')"
                :aria-label="$t('share')"
                class="btn-secondary">
          <i class="fas fa-share-nodes" aria-hidden="true"></i>
          <span class="btn-label-text">{{ $t('share') }}</span>
        </button>
      </div>

      <div class="header-divider"></div>

      <div class="header-utils">
        <button @click="toggleLight"
                :title="darkMode ? $t('light_mode') : $t('dark_mode')"
                :aria-label="darkMode ? $t('light_mode') : $t('dark_mode')"
                class="util-btn">
          <i :class="['fas', darkMode ? 'fa-sun' : 'fa-moon']" aria-hidden="true"></i>
        </button>
        <button @click="showSettingsModal"
                :title="$t('options')"
                :aria-label="$t('options')"
                class="util-btn">
          <i class="fas fa-cog" aria-hidden="true"></i>
        </button>
      </div>
    </header>

    <Body :lineHeight="lineHeight" :fontSize="fontSize" :isLoading="isLoading"
          :authorText="authorText" :bodyText="bodyText" :titleText="titleText"
          :sendReaction="sendReaction"
          :shareStory="shareStory"
          :errorMessage="errorMessage"
          :isRateLimited="isRateLimited"
          :onRetry="retryLastAction"
          :styles="darkMode ? 'darkMode' : 'lightMode'" />

    <!-- Enhanced Audio Player -->
    <div v-if="audio && bodyText" :class="['audio-player-container', darkMode ? 'darkMode' : 'lightMode']">
      <div class="audio-player">
        <button @click="togglePlayPause" class="audio-control-btn" :aria-label="speaking ? $t('stop_audio') : $t('listen_story')">
          <i :class="['fas', speaking ? 'fa-pause' : 'fa-play']" aria-hidden="true"></i>
        </button>

        <button @click="skipBackward" class="audio-control-btn" :aria-label="$t('skip_backward')">
          <i class="fas fa-backward" aria-hidden="true"></i>
        </button>

        <div class="audio-progress-container">
          <span class="audio-time">{{ formatTime(audioCurrentTime) }}</span>
          <input type="range"
                 class="audio-progress"
                 min="0"
                 :max="audioDuration"
                 :value="audioCurrentTime"
                 @input="seekAudio"
                 :aria-label="$t('audio_progress')" />
          <span class="audio-time">{{ formatTime(audioDuration) }}</span>
        </div>

        <button @click="skipForward" class="audio-control-btn" :aria-label="$t('skip_forward')">
          <i class="fas fa-forward" aria-hidden="true"></i>
        </button>

        <div class="playback-speed">
          <button @click="cyclePlaybackSpeed" class="speed-btn" :aria-label="$t('playback_speed')">
            {{ playbackSpeed }}x
          </button>
        </div>

        <div class="volume-container">
          <i class="fas fa-volume-up" aria-hidden="true"></i>
          <input type="range"
                 class="volume-slider"
                 min="0"
                 max="100"
                 :value="audioVolume"
                 @input="changeVolume"
                 :aria-label="$t('volume')" />
        </div>
      </div>
    </div>

    <Settings v-if="modalSettingsVisible" @close="hideSettingsModal"
        @font_plus="fontSize = Math.min(fontSize+2, 30);
                     lineHeight = Math.min(lineHeight+2, 35);"
        @font_less="fontSize = Math.max(fontSize-2, 16);
                    lineHeight = Math.max(lineHeight-2, 25);"
        @download_offline="downloadStoriesForOffline"
        @clear_offline="clearOfflineStories"
        :offlineStoriesCount="offlineStoriesCount"
        :isDownloadingOffline="isDownloadingOffline"
        :downloadProgress="downloadProgress"
        >
    </Settings>

    <Recommendations v-if="modalRecommendationsVisible" @close="hideRecommendationsModal"
          :recommendations="recommendations"
          :langValue="langValue"
          :storyId="storyId"
          :reaction="lastReaction"
          >
    </Recommendations>

    <!-- Crawlable entry points into the generated catalogue. The app has no
         other internal links, so without this the story pages are orphans
         discoverable only via the sitemap. -->
    <footer :class="['site-footer', darkMode ? 'darkMode' : 'lightMode']">
      <a href="/es/relatos/" hreflang="es">Relatos en español</a>
      <a href="/en/stories/" hreflang="en">Short stories in English</a>
      <a href="/fr/histoires/" hreflang="fr">Nouvelles en français</a>
      <a href="/es/autores/" hreflang="es">Autores</a>
    </footer>

    <!-- PWA Install Banner -->
    <InstallBanner :darkMode="darkMode" />
  </div>
</template>

<script>
import Body from './components/Body.vue'
import Settings from '@/components/Settings'
import Recommendations from './components/Recommendations.vue';
import InstallBanner from './components/InstallBanner.vue';
import CustomSelect from './components/CustomSelect.vue';
import axios from 'axios';
import offlineStorage from './utils/offlineStorage';

export default {
  name: 'App',
  components: {
    Body,
    Settings,
    Recommendations,
    InstallBanner,
    CustomSelect
  },
  data() {
    return {
      authorText: this.getData('authorText') || "",
      bodyText: this.getData('bodyText') || "",
      titleText: this.getData('titleText') || this.$t('title_text'),
      storyId: this.getData('storyId') || 0,
      audio: this.getData('audio') ||  "",
      langValue: this.getData('langValue') || "es-ES",
      selectedGenre: this.getData('selectedGenre') || "any",

      modalSettingsVisible: false,
      speaking: false,
      user_id: "",
      readingTime: parseInt(this.getData('readingTime')) || 5,
      showReactions: this.getData('showReactions') === 'true',
      readingSelectorVisible: false,
      langSelectorVisible: false,

      darkMode: this.getInitialDarkMode(),
      fontSize: parseInt(this.getData('fontSize')) || 16,
      lineHeight: parseInt(this.getData('lineHeight')) || 25,
      isLoading: false,
      modalRecommendationsVisible: false,
      recommendations: [],
      lastReaction: null,
      errorMessage: "",
      isRateLimited: false,
      lastAction: null,
      scrollProgress: 0,
      audioCurrentTime: 0,
      audioDuration: 0,
      audioVolume: 100,
      playbackSpeed: 1,
      audioUpdateInterval: null,
      offlineStoriesCount: 0,
      isDownloadingOffline: false,
      downloadProgress: 0,
      showShareNotification: false
    }
  },

  created() {
    // On a static story page (/es/relato/<slug>/), main.js has already read the
    // story out of the server-rendered markup. Adopt it so the reader keeps
    // seeing the story they landed on instead of the welcome screen.
    const preloaded = window.__LEGI_STORY__;
    if (preloaded && preloaded.body) {
      this.titleText = preloaded.title;
      this.bodyText = preloaded.body;
      this.authorText = preloaded.author;
      this.storyId = preloaded.id;
      this.audio = preloaded.audio;
      this.showReactions = true;
      this.errorMessage = "";
      if (preloaded.lang) this.langValue = preloaded.lang;
      if (preloaded.readingTime) this.readingTime = preloaded.readingTime;
      this.storeData('titleText', this.titleText);
      this.storeData('bodyText', this.bodyText);
      this.storeData('authorText', this.authorText);
      this.storeData('storyId', this.storyId);
      this.storeData('audio', this.audio);
      this.storeData('showReactions', this.showReactions);
    }
  },

  async mounted() {
    document.addEventListener('keydown', this.handleKeyboardShortcuts);
    window.addEventListener('scroll', this.updateScrollProgress);
    await this.initOfflineStorage();
  },

  beforeDestroy() {
    document.removeEventListener('keydown', this.handleKeyboardShortcuts);
    window.removeEventListener('scroll', this.updateScrollProgress);
  },

  watch: {
    $route(newVal) {
      this.modalSettingsVisible = newVal.query?.settings || false
    },
    darkMode(newVal) {
      this.storeData('darkMode', newVal);
    },
    fontSize(newVal) {
      this.storeData('fontSize', newVal);
    },
    lineHeight(newVal) {
      this.storeData('lineHeight', newVal);
    },
    langValue(newVal) {
      this.storeData('langValue', newVal);
    },
    readingTime(newVal) {
      this.storeData('readingTime', newVal);
    },
    selectedGenre(newVal) {
      this.storeData('selectedGenre', newVal);
    }
  },

  computed: {
    readingTimeOptions() {
      return [
        { value: 5, label: '5 min' },
        { value: 10, label: '10 min' },
        { value: 15, label: '15 min' },
        { value: 20, label: '20 min' },
        { value: 25, label: '25 min' },
        { value: 30, label: '30 min' }
      ];
    },
    languageOptions() {
      return [
        { value: 'es-ES', label: this.$t('spanish') },
        { value: 'en-US', label: this.$t('english') },
        { value: 'fr-FR', label: this.$t('french') }
      ];
    },
    genreOptions() {
      return [
        { value: 'any', label: this.$t('any_genre') },
        { value: 'Fiction (short story, narrative)', label: this.$t('genre_fiction') },
        { value: 'Poetry (poem, verse)', label: this.$t('genre_poetry') },
        { value: 'Fable (moral tale)', label: this.$t('genre_fable') },
        { value: 'Legend (folklore)', label: this.$t('genre_legend') },
        { value: 'Essay (non-fiction prose)', label: this.$t('genre_essay') },
        { value: 'Drama (theater, play)', label: this.$t('genre_drama') },
        { value: 'Horror', label: this.$t('genre_horror') },
        { value: 'Science Fiction', label: this.$t('genre_scifi') },
        { value: 'Fantasy', label: this.$t('genre_fantasy') },
        { value: 'Mystery', label: this.$t('genre_mystery') },
        { value: 'Historical Fiction', label: this.$t('genre_historical') }
      ];
    }
  },

  methods: {
    getInitialDarkMode() {
      const savedPreference = this.getData('darkMode');
      if (savedPreference !== "") {
        return savedPreference === 'true';
      }
      if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
        return true;
      }
      return false;
    },

    updateScrollProgress() {
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const trackLength = documentHeight - windowHeight;
      const progress = (scrollTop / trackLength) * 100;
      this.scrollProgress = Math.min(100, Math.max(0, progress));
    },

    handleKeyboardShortcuts(event) {
      if (event.target.tagName === 'INPUT' || event.target.tagName === 'SELECT' || event.target.tagName === 'TEXTAREA') {
        return;
      }
      if (event.key === 'Escape') {
        if (this.modalSettingsVisible) {
          this.hideSettingsModal();
        } else if (this.modalRecommendationsVisible) {
          this.hideRecommendationsModal();
        }
        return;
      }
      if (this.modalSettingsVisible || this.modalRecommendationsVisible) {
        return;
      }
      switch(event.key.toLowerCase()) {
        case 'd':
          this.toggleLight();
          event.preventDefault();
          break;
        case 's':
          this.showSettingsModal();
          event.preventDefault();
          break;
        case 'n':
          this.getStory();
          event.preventDefault();
          break;
      }
    },

    retryLastAction() {
      this.errorMessage = "";
      if (this.lastAction) {
        this.lastAction();
      }
    },

    toggleLight() {
      this.darkMode = !this.darkMode;
    },

    showSettingsModal() {
      this.modalSettingsVisible = true;
      this.$router.push({
        name : "App",
        query : {
          settings: true
        }
      })
    },

    hideSettingsModal() {
      this.modalSettingsVisible = false;
      this.$router.push({
        name : "App"
      })
    },

    showRecommendationsModal() {
      this.modalRecommendationsVisible = true;
    },

    hideRecommendationsModal() {
      this.modalRecommendationsVisible = false;
    },

    get_user_random_id() {
      function create_UUID(){
          var dt = new Date().getTime();
          var uuid = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
              var r = (dt + Math.random()*16)%16 | 0;
              dt = Math.floor(dt/16);
              return (c=='x' ? r :(r&0x3|0x8)).toString(16);
          });
          return uuid;
      }

      let uid = this.getData('user_random_id');

      if (uid=="") {
        uid = create_UUID();
        this.storeData('user_random_id', uid);
        this.user_id = uid
      }

      return uid;
    },

    storeData(key, value) {
      localStorage.setItem(key, value)
    },

    getData(key) {
      try {
        const value = localStorage.getItem(key)
        if(value !== null) {
          return value;
        }
      } catch(e) {
        console.log(e);
      }
      return "";
    },

    playAudio() {
      if (!this.speaker) {
        this.speaker = new Audio(this.audio);
        this.speaker.addEventListener('ended', () => { this.stopAudio() });
        this.speaker.addEventListener('loadedmetadata', () => {
          this.audioDuration = this.speaker.duration;
        });
        this.speaker.addEventListener('timeupdate', () => {
          this.audioCurrentTime = this.speaker.currentTime;
        });
        this.speaker.volume = this.audioVolume / 100;
        this.speaker.playbackRate = this.playbackSpeed;
      }
      this.speaker.play();
      this.speaking = true;
    },

    stopAudio() {
      if (this.speaker) {
        this.speaker.pause();
      }
      this.speaking = false;
    },

    togglePlayPause() {
      if (this.speaking) {
        this.stopAudio();
      } else {
        this.playAudio();
      }
    },

    skipBackward() {
      if (this.speaker) {
        this.speaker.currentTime = Math.max(0, this.speaker.currentTime - 10);
      }
    },

    skipForward() {
      if (this.speaker) {
        this.speaker.currentTime = Math.min(this.speaker.duration, this.speaker.currentTime + 10);
      }
    },

    cyclePlaybackSpeed() {
      const speeds = [0.75, 1, 1.25, 1.5];
      const currentIndex = speeds.indexOf(this.playbackSpeed);
      const nextIndex = (currentIndex + 1) % speeds.length;
      this.playbackSpeed = speeds[nextIndex];
      if (this.speaker) {
        this.speaker.playbackRate = this.playbackSpeed;
      }
    },

    changeVolume(event) {
      this.audioVolume = event.target.value;
      if (this.speaker) {
        this.speaker.volume = this.audioVolume / 100;
      }
    },

    seekAudio(event) {
      if (this.speaker) {
        this.speaker.currentTime = event.target.value;
      }
    },

    formatTime(seconds) {
      if (isNaN(seconds) || seconds === 0) return '0:00';
      const mins = Math.floor(seconds / 60);
      const secs = Math.floor(seconds % 60);
      return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
    },

    async initOfflineStorage() {
      try {
        await offlineStorage.init();
        this.offlineStoriesCount = await offlineStorage.getStoryCount();
      } catch (error) {
        console.error('Failed to initialize offline storage:', error);
      }
    },

    async downloadStoriesForOffline(count = 5) {
      if (this.isDownloadingOffline) return;

      this.isDownloadingOffline = true;
      this.downloadProgress = 0;

      try {
        const uid = this.get_user_random_id();

        for (let i = 0; i < count; i++) {
          const response = await axios.post(
            'https://us-central1-shortstories-292802.cloudfunctions.net/get_story',
            {
              lang: this.langValue,
              uid: uid,
              reading_time: this.readingTime,
              genre: this.selectedGenre
            }
          );

          const json = response.data;

          if (json && !json.error) {
            const storyId = await offlineStorage.saveStory({
              title: json.title,
              body: json.body,
              author: json.author,
              storyId: json.id,
              audioUrl: json.audio,
              lang: this.langValue,
              readingTime: this.readingTime
            });

            if (json.audio) {
              try {
                const audioResponse = await fetch(json.audio);
                const audioBlob = await audioResponse.blob();
                await offlineStorage.saveAudio(storyId, audioBlob);
              } catch (audioError) {
                console.warn('Failed to download audio:', audioError);
              }
            }
          }

          this.downloadProgress = Math.round(((i + 1) / count) * 100);
        }

        this.offlineStoriesCount = await offlineStorage.getStoryCount();
        alert(this.$t('offline_download_complete') + ' (' + count + ' ' + this.$t('stories') + ')');
      } catch (error) {
        console.error('Failed to download stories:', error);
        alert(this.$t('offline_download_failed'));
      } finally {
        this.isDownloadingOffline = false;
        this.downloadProgress = 0;
      }
    },

    async loadOfflineStory() {
      try {
        const story = await offlineStorage.getOldestStory();

        if (!story) {
          this.errorMessage = this.$t('no_offline_stories');
          return false;
        }

        this.titleText = story.title;
        this.bodyText = story.body;
        this.authorText = story.author;
        this.storyId = story.storyId;
        this.showReactions = false;
        this.errorMessage = "";

        const audioBlob = await offlineStorage.getAudio(story.id);
        if (audioBlob) {
          this.audio = URL.createObjectURL(audioBlob);
        } else {
          this.audio = "";
        }

        this.storeData('titleText', this.titleText);
        this.storeData('bodyText', this.bodyText);
        this.storeData('authorText', this.authorText);
        this.storeData('storyId', this.storyId);

        await offlineStorage.deleteStory(story.id);
        if (audioBlob) {
          await offlineStorage.deleteAudio(story.id);
        }

        this.offlineStoriesCount = await offlineStorage.getStoryCount();
        return true;
      } catch (error) {
        console.error('Failed to load offline story:', error);
        return false;
      }
    },

    async clearOfflineStories() {
      try {
        await offlineStorage.clearAll();
        this.offlineStoriesCount = 0;
        alert(this.$t('offline_stories_cleared'));
      } catch (error) {
        console.error('Failed to clear offline stories:', error);
      }
    },

    async getStory() {
      this.lastAction = this.getStory;

      if (!navigator.onLine) {
        this.isLoading = true;
        const loaded = await this.loadOfflineStory();
        this.isLoading = false;

        if (!loaded) {
          this.errorMessage = this.$t("reach_internet");
        }
        return;
      }

      if (this.speaking != "")
        this.speaker.pause();

      if (this.speaker) {
        this.speaker.pause();
        this.speaker = null;
      }
      this.speaking = false;
      this.audioCurrentTime = 0;
      this.audioDuration = 0;

      this.isLoading = true;
      this.errorMessage = "";
      this.firstRun = false;
      this.bodyText = "";
      this.authorText = "";
      this.titleText = "";
      this.showReactions = false;

      let uid = this.get_user_random_id();

      if ( (this.langValue == "")) {
        this.errorMessage = this.$t("choose_language");
        this.isLoading = false;
        return;
      }

      axios.post('https://us-central1-shortstories-292802.cloudfunctions.net/get_story',
                 {
                  lang: this.langValue,
                  uid: uid,
                  reading_time: this.readingTime,
                  genre: this.selectedGenre
                })
                .then((response) => response.data)
                .then((json) => {
                  if (json == undefined) {
                    this.errorMessage = this.$t("error_tryagain");
                    this.isLoading = false;
                    return;
                  }
                  if ("error" in json) {
                    const err = json["error"] || "";
                    if (/Too many requests/i.test(err))
                      // Retrying is exactly the wrong action here, so the retry
                      // button is suppressed for this error (see isRateLimited).
                      this.errorMessage = this.$t("rate_limited");
                    else if (/No stor(y|ies)/i.test(err))
                      // Regex, not indexOf("No story"): the genre-miss path
                      // returns "No stories available for genre: ...", which the
                      // old check never matched.
                      this.errorMessage = this.$t("nostory_tryagain");
                    else
                      this.errorMessage = this.$t("error_tryagain");

                    this.isRateLimited = /Too many requests/i.test(err);
                    this.isLoading = false;
                  }
                  else {
                    this.titleText = json.title;
                    this.bodyText = json.body;
                    this.authorText = json.author;
                    this.storyId = json.id;
                    this.audio = json.audio;
                    this.showReactions = true;
                    this.errorMessage = "";
                    this.isRateLimited = false;

                    this.isLoading = false;

                    this.storeData('titleText', this.titleText);
                    this.storeData('bodyText', this.bodyText);
                    this.storeData('authorText', this.authorText);
                    this.storeData('storyId', this.storyId);
                    this.storeData('audio', this.audio);
                    this.storeData('showReactions', this.showReactions);
                  }
                })
                .catch((error) => {
                  console.error(error);
                  this.isLoading = false;
                  if (error.message === 'Network Error' || !navigator.onLine) {
                    this.errorMessage = this.$t("network_error");
                  } else {
                    this.errorMessage = this.$t("error_tryagain");
                  }
                })
    },

    sendReaction(reaction) {
      let uid = this.get_user_random_id();
      let story_id = this.storyId;
      // Saving a reaction never required wiping the story. Blanking the text and
      // setting isLoading before the POST meant any failure left Body.vue's v-if
      // chain stuck on the spinner branch forever, with the retry button
      // unreachable behind it and the story gone.
      this.lastReaction = reaction;
      this.showReactions = false;

      axios.post('https://us-central1-shortstories-292802.cloudfunctions.net/save_reaction',
                 {
                  reaction: reaction,
                  uid: uid,
                  story_id: story_id,
                  // The reading language the user chose, not the browser's:
                  // the backend picks the recommendation language from this.
                  ui_lang: this.langValue
                 })
            .then((response) => response.data)
            .then((json) => {
              // Defensive: Recommendations reads .length, so a response without
              // the key would otherwise throw during render.
              this.recommendations = (json && json.recommendations) || [];
              this.modalRecommendationsVisible = true;
            })
            .catch((error) => {
              // The reaction is fire-and-forget from the reader's point of view.
              // Never trap them behind a failed recommendation lookup.
              console.error(error);
            });
    },

    async shareStory() {
      const shareText = this.$t('share_text', { title: this.titleText, author: this.authorText });
      const shareUrl = 'https://legi.app';

      if (navigator.share) {
        try {
          await navigator.share({
            title: this.titleText,
            text: shareText,
            url: shareUrl
          });
        } catch (err) {
          if (err.name !== 'AbortError') {
            console.error('Share failed:', err);
          }
        }
      } else {
        try {
          await navigator.clipboard.writeText(shareText + ' - ' + shareUrl);
        } catch (err) {
          const textArea = document.createElement('textarea');
          textArea.value = shareText + ' - ' + shareUrl;
          textArea.style.position = 'fixed';
          textArea.style.opacity = '0';
          document.body.appendChild(textArea);
          textArea.select();
          document.execCommand('copy');
          document.body.removeChild(textArea);
        }
        this.showShareNotification = true;
        setTimeout(() => { this.showShareNotification = false; }, 2500);
      }
    }
  }
}
</script>

<style>
  #app {
    font-family: 'Roboto', 'Helvetica Neue', Arial, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    text-align: center;
    width: 100%;
    min-height: 100vh;
    font-size: 16px;
    transition: background-color 0.3s ease, color 0.3s ease;
  }

  .progress-bar {
    position: fixed;
    top: 0;
    left: 0;
    height: 4px;
    background: linear-gradient(90deg, #3498db, #2ecc71);
    z-index: 10000;
    transition: width 0.1s ease-out;
    box-shadow: 0 2px 4px rgba(52, 152, 219, 0.3);
  }

  /* ====== Header ====== */
  .app-header {
    display: flex;
    align-items: center;
    gap: 16px;
    padding: 12px 24px;
    border-bottom: 1px solid;
  }

  .lightMode .app-header {
    border-color: #e0e4ea;
  }

  .darkMode .app-header {
    border-color: #3a3a55;
  }

  .site-footer {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 8px 20px;
    padding: 28px 20px 96px;
    font-size: 0.86rem;
  }

  .site-footer a {
    color: inherit;
    opacity: 0.7;
    text-decoration: underline;
  }

  .site-footer a:hover {
    opacity: 1;
  }

  .header-logo {
    width: 48px;
    height: auto;
    flex-shrink: 0;
  }

  .darkMode .header-logo {
    filter: brightness(2) saturate(0.7);
  }

  .header-selects {
    display: flex;
    gap: 8px;
    flex: 1;
    min-width: 0;
  }

  .header-actions {
    display: flex;
    gap: 6px;
    align-items: center;
  }

  .header-divider {
    width: 1px;
    height: 28px;
    background: rgba(128, 128, 128, 0.25);
    flex-shrink: 0;
  }

  .header-utils {
    display: flex;
    gap: 4px;
    align-items: center;
  }

  /* ====== Buttons ====== */
  .btn-get-story {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 18px;
    background: #3498db;
    color: white;
    border: none;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 600;
    font-family: inherit;
    cursor: pointer;
    transition: all 0.2s ease;
    white-space: nowrap;
  }

  .btn-get-story i {
    font-size: 16px;
  }

  .btn-get-story:hover {
    background: #2980b9;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(52, 152, 219, 0.3);
  }

  .btn-get-story:active {
    transform: translateY(0);
  }

  .btn-get-story:focus {
    outline: 2px solid #3498db;
    outline-offset: 2px;
  }

  .btn-secondary {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 8px 14px;
    background: none;
    border: 2px solid #c0c8d4;
    border-radius: 8px;
    font-size: 14px;
    font-family: inherit;
    cursor: pointer;
    transition: all 0.2s ease;
    white-space: nowrap;
    color: inherit;
  }

  .btn-secondary:hover {
    border-color: #3498db;
    color: #3498db;
  }

  .btn-secondary:focus {
    outline: 2px solid #3498db;
    outline-offset: 2px;
  }

  .btn-stop {
    border-color: #e74c3c;
    color: #e74c3c;
  }

  .btn-stop:hover {
    background: #e74c3c;
    color: white;
  }

  .darkMode .btn-secondary {
    border-color: #3a3a55;
  }

  .darkMode .btn-secondary:hover {
    border-color: #5dade2;
    color: #5dade2;
  }

  .util-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    background: none;
    border: none;
    border-radius: 8px;
    font-size: 18px;
    cursor: pointer;
    transition: all 0.2s ease;
    color: inherit;
  }

  .util-btn:hover {
    background: rgba(0, 0, 0, 0.06);
    color: #3498db;
  }

  .darkMode .util-btn:hover {
    background: rgba(255, 255, 255, 0.08);
    color: #5dade2;
  }

  .util-btn:focus {
    outline: 2px solid #3498db;
    outline-offset: 2px;
  }

  /* ====== Theme ====== */
  .darkMode {
    color: #e8e8e8;
    background-color: #1a1a2e;
    border-color: #3a3a55;
  }

  .lightMode {
    color: #00073F;
    background-color: #F0F4F8;
    border-color: #e0e4ea;
  }

  /* ====== Share Toast ====== */
  .share-toast {
    position: fixed;
    top: 20px;
    left: 50%;
    transform: translateX(-50%);
    background: #2ecc71;
    color: white;
    padding: 12px 24px;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 600;
    z-index: 10001;
    box-shadow: 0 4px 16px rgba(46, 204, 113, 0.4);
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .toast-enter-active {
    animation: toast-in 0.3s ease;
  }

  .toast-leave-active {
    animation: toast-in 0.3s ease reverse;
  }

  @keyframes toast-in {
    from {
      opacity: 0;
      transform: translateX(-50%) translateY(-16px);
    }
    to {
      opacity: 1;
      transform: translateX(-50%) translateY(0);
    }
  }

  /* ====== Dark Mode Modal Overrides ====== */
  .darkMode .modal-container {
    background-color: #252540;
    color: #e8e8e8;
    box-shadow: 0 2px 20px rgba(0, 0, 0, 0.5);
  }

  .darkMode .settings-section {
    background-color: #1e1e35;
  }

  .darkMode .section-title {
    color: #e8e8e8;
  }

  .darkMode .offline-section {
    background-color: #1e1e35;
    border-color: #3a3a55;
  }

  .darkMode .offline-title {
    color: #e8e8e8;
  }

  .darkMode .offline-count {
    color: #ccc;
  }

  .darkMode .offline-hint {
    color: #999;
  }

  .darkMode .buttons {
    color: #e8e8e8;
    border-color: #3a3a55;
  }

  /* ====== Audio Player ====== */
  .audio-player-container {
    position: fixed;
    bottom: 0;
    left: 0;
    width: 100%;
    z-index: 1000;
    box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.1);
    transition: background-color 0.3s ease;
  }

  .audio-player-container.darkMode {
    background-color: #15152a;
    border-top: 1px solid #3a3a55;
  }

  .audio-player-container.lightMode {
    background-color: #ffffff;
    border-top: 1px solid #e0e4ea;
  }

  .audio-player {
    display: flex;
    align-items: center;
    gap: 15px;
    padding: 15px 20px;
    max-width: 1200px;
    margin: 0 auto;
  }

  .audio-control-btn {
    background: none;
    border: none;
    font-size: 24px;
    cursor: pointer;
    transition: transform 0.2s, color 0.3s;
    outline: none;
    padding: 8px;
    color: inherit;
  }

  .audio-control-btn:hover {
    transform: scale(1.1);
    color: #3498db;
  }

  .audio-control-btn:focus {
    outline: 2px solid #3498db;
    outline-offset: 2px;
    border-radius: 4px;
  }

  .audio-progress-container {
    flex: 1;
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .audio-time {
    font-size: 12px;
    min-width: 40px;
    text-align: center;
  }

  .audio-progress {
    flex: 1;
    -webkit-appearance: none;
    appearance: none;
    height: 6px;
    border-radius: 3px;
    background: #ddd;
    outline: none;
    cursor: pointer;
  }

  .darkMode .audio-progress {
    background: #3a3a55;
  }

  .audio-progress::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background: #3498db;
    cursor: pointer;
    transition: transform 0.2s;
  }

  .audio-progress::-moz-range-thumb {
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background: #3498db;
    cursor: pointer;
    border: none;
    transition: transform 0.2s;
  }

  .audio-progress::-webkit-slider-thumb:hover,
  .audio-progress::-moz-range-thumb:hover {
    transform: scale(1.2);
  }

  .playback-speed {
    min-width: 50px;
  }

  .speed-btn {
    background: #3498db;
    color: white;
    border: none;
    padding: 6px 12px;
    border-radius: 4px;
    cursor: pointer;
    font-size: 12px;
    font-weight: bold;
    transition: background-color 0.3s;
  }

  .speed-btn:hover {
    background: #2980b9;
  }

  .speed-btn:focus {
    outline: 2px solid #3498db;
    outline-offset: 2px;
  }

  .volume-container {
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 120px;
  }

  .volume-container i {
    font-size: 18px;
  }

  .volume-slider {
    -webkit-appearance: none;
    appearance: none;
    width: 80px;
    height: 4px;
    border-radius: 2px;
    background: #ddd;
    outline: none;
    cursor: pointer;
  }

  .darkMode .volume-slider {
    background: #3a3a55;
  }

  .volume-slider::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 12px;
    height: 12px;
    border-radius: 50%;
    background: #3498db;
    cursor: pointer;
  }

  .volume-slider::-moz-range-thumb {
    width: 12px;
    height: 12px;
    border-radius: 50%;
    background: #3498db;
    cursor: pointer;
    border: none;
  }

  /* ====== Responsive ====== */
  @media (max-width: 768px) {
    .app-header {
      flex-wrap: wrap;
      padding: 10px 12px;
      gap: 10px;
    }

    .header-logo {
      width: 36px;
      order: 1;
    }

    .header-utils {
      order: 2;
      margin-left: auto;
    }

    .header-divider {
      display: none;
    }

    .header-selects {
      order: 3;
      flex-basis: 100%;
      flex: none;
    }

    .header-actions {
      order: 4;
      flex-basis: 100%;
      flex-wrap: wrap;
    }

    .btn-get-story {
      flex: 1;
      justify-content: center;
    }

    .btn-secondary {
      flex: 1;
      justify-content: center;
    }

    .btn-label-text {
      display: none;
    }

    .audio-player {
      flex-wrap: wrap;
      gap: 10px;
      padding: 12px 15px;
    }

    .audio-progress-container {
      order: 3;
      flex-basis: 100%;
    }

    .volume-container {
      min-width: 100px;
    }

    .volume-slider {
      width: 60px;
    }
  }
</style>
